import React, { FC, useEffect, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';

import { isEmpty } from 'lodash';

import {
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Box,
  Checkbox,
  Alert,
  AlertTitle,
  Dialog,
  DialogContent,
  DialogActions,
  Autocomplete,
} from '@mui/material';

import { useFetchGames, useCreateKey } from './create-key-form.actions';
import type {
  CreateKeyFormInputs,
  CreateKeyFormProps,
  MasterAvailableGames,
} from './create-key-form.types';

import styles from './create-key-form.module.scss';

const {
  formContainer,
  errorContainer,
  textareaContainer,
  whiteLabelCheckboxContainer,
} = styles;

const CreateKeyForm: FC<CreateKeyFormProps> = ({ refetch }) => {
  const [gameKey, handleCreateKeySubmit, loading] = useCreateKey();
  const [games] = useFetchGames();
  const [selectedGame, setSelectedGame] = useState<MasterAvailableGames | null>(null);
  const [selectedPreview, setSelectedPreview] = useState<string | null>(null);

  const {
    handleSubmit,
    formState: { errors },
    control,
  } = useForm<CreateKeyFormInputs>();

  useEffect(() => {
    if (gameKey) {
      refetch();
    }
  }, [gameKey]);


  const onSubmit = (data: CreateKeyFormInputs) => {
    handleCreateKeySubmit(data);
  };

  // Сортируем игры: Scary Maze Limited первая, остальные по алфавиту
  const sortedGames = useMemo(() => {
    const list = Array.isArray(games) ? games : [];
    return list.slice().sort((a, b) => {
      // Scary Maze Limited всегда первая
      if (a.name === 'Scary Maze Limited') return -1;
      if (b.name === 'Scary Maze Limited') return 1;
      
      // Остальные игры сортируем по алфавиту
      return a.name.localeCompare(b.name, 'ru');
    });
  }, [games]);

  return (
    <>
      <Typography variant="h5" noWrap component="h3" sx={{ display: 'none' }}>
        Create Key
      </Typography>
      <form onSubmit={(...args) => void handleSubmit(onSubmit)(...args)}>
        <Box className={`create-key-inline-form`}>
          <FormControl sx={{ width: '100%' }} className="field--game">
            <Controller
              name="gameName"
              control={control}
              rules={{ required: true }}
              defaultValue=""
              render={({ field }) => (
                <Autocomplete
                  options={sortedGames}
                  getOptionLabel={(option) => option.name || option.title}
                  value={sortedGames.find(game => game.title === field.value) || null}
                  onChange={(event, newValue) => {
                    field.onChange(newValue?.title || '');
                    setSelectedGame(newValue || null);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Game"
                      error={!!errors?.gameName}
                      size="small"
                      variant="filled"
                    />
                  )}
                  isOptionEqualToValue={(option, value) => option.title === value.title}
                />
              )}
            />
          </FormControl>
          <FormControl sx={{ width: '100%' }} className="field--edit-days">
            <Controller
              name="editDayCount"
              defaultValue={7}
              control={control}
              rules={{ required: true, validate: (value) => value > 0 }}
              render={({ field }) => (
                <TextField
                  {...field}
                  size="small"
                  type="number"
                  error={!!errors?.editDayCount}
                  label="Enter edit day count"
                />
              )}
            />
          </FormControl>
          <FormControl sx={{ width: '100%' }} className="field--game-days">
            <Controller
              name="gameDayCount"
              defaultValue={365}
              control={control}
              rules={{ required: true, validate: (value) => value > 0 }}
              render={({ field }) => (
                <TextField
                  {...field}
                  size="small"
                  type="number"
                  error={!!errors?.gameDayCount}
                  label="Enter game day count"
                />
              )}
            />
          </FormControl>
          <FormControl sx={{ width: '100%' }} className="field--count">
            <Controller
              name="gamesCount"
              defaultValue={1}
              control={control}
              rules={{ required: true, validate: (value) => value > 0 }}
              render={({ field }) => (
                <TextField
                  {...field}
                  size="small"
                  type="number"
                  error={!!errors?.gamesCount}
                  label="Enter games count"
                />
              )}
            />
          </FormControl>
          <FormControl sx={{ width: '100%' }} className="field--notion">
            <Controller
              name="notion"
              defaultValue=""
              control={control}
              rules={{ required: false }}
              render={({ field }) => (
                <TextField
                  {...field}
                  size="small"
                  inputProps={{
                    maxLength: 200,
                  }}
                  error={!!errors?.notion}
                  label="Enter notion"
                />
              )}
            />
          </FormControl>
          <FormControl className="field--whitelabel">
            <Controller
              name="isWhiteLabel"
              defaultValue={false}
              control={control}
              render={({ field }) => (
                <Box className={whiteLabelCheckboxContainer}>
                  <Checkbox {...field} size="small" />
                  <Typography fontSize="14px">White Label</Typography>
                </Box>
              )}
            />
          </FormControl>
          {selectedGame && (
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.5, mt: 2, justifyContent: 'center', width:'100px'}}>
              {selectedGame.startGameImageExampleUrl && (
                <img
                  src={Array.isArray(selectedGame.startGameImageExampleUrl) ? selectedGame.startGameImageExampleUrl[0] : selectedGame.startGameImageExampleUrl}
                  alt="Start preview"
                  style={{ width: 45, height: 80, objectFit: 'cover', borderRadius: 2, cursor: 'pointer' }}
                  onClick={() => setSelectedPreview(Array.isArray(selectedGame.startGameImageExampleUrl) ? selectedGame.startGameImageExampleUrl[0] : selectedGame.startGameImageExampleUrl)}
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              )}
              {selectedGame.endGameImageExampleUrl && (
                <img
                  src={Array.isArray(selectedGame.endGameImageExampleUrl) ? selectedGame.endGameImageExampleUrl[0] : selectedGame.endGameImageExampleUrl}
                  alt="End preview"
                  style={{ width: 45, height: 80, objectFit: 'cover', borderRadius: 2, cursor: 'pointer' }}
                  onClick={() => setSelectedPreview(Array.isArray(selectedGame.endGameImageExampleUrl) ? selectedGame.endGameImageExampleUrl[0] : selectedGame.endGameImageExampleUrl)}
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              )}
              {selectedGame.gameTileExampleUrl && (
                <img
                  src={Array.isArray(selectedGame.gameTileExampleUrl) ? selectedGame.gameTileExampleUrl[0] : selectedGame.gameTileExampleUrl}
                  alt="Tile preview"
                  style={{ width: 45, height: 80, objectFit: 'cover', borderRadius: 2, cursor: 'pointer' }}
                  onClick={() => setSelectedPreview(Array.isArray(selectedGame.gameTileExampleUrl) ? selectedGame.gameTileExampleUrl[0] : selectedGame.gameTileExampleUrl)}
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              )}
            </Box>
          )}
          <Box className="field--submit" sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
            <Button 
              disabled={loading} 
              size="large" 
              type="submit" 
              variant="contained"
              sx={{ 
                fontSize: '18px', 
                padding: '12px 32px',
                fontWeight: 'bold'
              }}
            >
              СОЗДАТЬ КЛЮЧ
            </Button>
          </Box>
        </Box>
        {!isEmpty(errors) && (
          <Box className={errorContainer}>
            <Alert severity="error">
              <AlertTitle>Error</AlertTitle>
              Make sure all required fields are filled.
            </Alert>
          </Box>
        )}
        {selectedPreview && (
          <Dialog open onClose={() => setSelectedPreview(null)} maxWidth="md" fullWidth>
            <DialogContent>
              <img src={selectedPreview} style={{ width: '100%', maxHeight: '80vh', objectFit: 'contain' }} alt="Preview" />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedPreview(null)}>Закрыть</Button>
            </DialogActions>
          </Dialog>
        )}
      </form>
    </>
  );
};

export default CreateKeyForm;
